import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  DoubleSide,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshStandardMaterial,
  OctahedronGeometry,
  PlaneGeometry,
  SphereGeometry,
  TorusGeometry
} from 'three';

export class JungleMeshFactory {
  /**
   * Ancient Golem (Blue Buff) - Stone elemental with glowing blue core, orbiting floating crystals & emissive aura.
   */
  static createBlueBuffMesh() {
    const group = new Group();
    const rockMat = new MeshStandardMaterial({ color: 0x334155, roughness: 0.8, metalness: 0.2 });
    const coreMat = new MeshStandardMaterial({ color: 0x38bdf8, emissive: 0x0284c7, emissiveIntensity: 0.9, roughness: 0.2 });
    const crystalMat = new MeshStandardMaterial({ color: 0xbae6fd, emissive: 0x38bdf8, emissiveIntensity: 0.7, roughness: 0.1 });

    // Rock Torso & Shoulders
    const torso = new Mesh(new DodecahedronGeometry(1.3, 1), rockMat);
    torso.position.y = 1.7;
    group.add(torso);

    const leftShoulder = new Mesh(new DodecahedronGeometry(0.65, 0), rockMat);
    leftShoulder.position.set(-1.3, 2.2, 0);
    group.add(leftShoulder);

    const rightShoulder = new Mesh(new DodecahedronGeometry(0.65, 0), rockMat);
    rightShoulder.position.set(1.3, 2.2, 0);
    group.add(rightShoulder);

    // Glowing Rune Core (Center)
    const core = new Mesh(new OctahedronGeometry(0.7), coreMat);
    core.position.y = 1.7;
    group.add(core);

    // Stone Head
    const head = new Mesh(new DodecahedronGeometry(0.55, 1), rockMat);
    head.position.y = 2.75;
    group.add(head);

    // Orbiting Floating Crystals (3 pieces)
    const crystalGroup = new Group();
    crystalGroup.position.y = 1.7;
    for (let i = 0; i < 3; i++) {
      const angle = (i * Math.PI * 2) / 3;
      const crystal = new Mesh(new OctahedronGeometry(0.25), crystalMat);
      crystal.position.set(Math.cos(angle) * 1.6, 0.4 * Math.sin(i), Math.sin(angle) * 1.6);
      crystalGroup.add(crystal);
    }
    group.add(crystalGroup);

    // Procedural Animation Update
    group.userData.update = (dt, elapsed = 0) => {
      core.rotation.y += dt * 1.5;
      crystalGroup.rotation.y += dt * 1.2;
      torso.position.y = 1.7 + Math.sin(elapsed * 2) * 0.08;
      head.position.y = 2.75 + Math.sin(elapsed * 2 + 0.5) * 0.06;
    };

    return group;
  }

  /**
   * Brambleback (Red Buff) - Ancient tree elemental with glowing magma cracks & embers.
   */
  static createRedBuffMesh() {
    const group = new Group();
    const barkMat = new MeshStandardMaterial({ color: 0x451a03, roughness: 0.95 });
    const emberMat = new MeshStandardMaterial({ color: 0xef4444, emissive: 0xd97706, emissiveIntensity: 0.9, roughness: 0.3 });
    const hornMat = new MeshStandardMaterial({ color: 0x78350f, roughness: 0.7 });

    // Bark Torso
    const torso = new Mesh(new CylinderGeometry(1.1, 0.75, 2.2, 8), barkMat);
    torso.position.y = 1.5;
    group.add(torso);

    // Glowing Magma Chest Core
    const magmaChest = new Mesh(new SphereGeometry(0.6, 12, 12), emberMat);
    magmaChest.position.set(0, 1.7, 0.4);
    group.add(magmaChest);

    // Ember Head & Wooden Horns
    const head = new Mesh(new IcosahedronGeometry(0.7, 1), emberMat);
    head.position.y = 2.85;
    group.add(head);

    const leftHorn = new Mesh(new ConeGeometry(0.2, 1.0, 6), hornMat);
    leftHorn.position.set(-0.55, 3.4, 0);
    leftHorn.rotation.z = Math.PI / 6;
    group.add(leftHorn);

    const rightHorn = new Mesh(new ConeGeometry(0.2, 1.0, 6), hornMat);
    rightHorn.position.set(0.55, 3.4, 0);
    rightHorn.rotation.z = -Math.PI / 6;
    group.add(rightHorn);

    // Procedural Animation Update
    group.userData.update = (dt, elapsed = 0) => {
      const pulse = 0.7 + 0.3 * Math.sin(elapsed * 4);
      emberMat.emissiveIntensity = pulse;
      torso.rotation.y = Math.sin(elapsed * 1.5) * 0.08;
    };

    return group;
  }

  /**
   * Scuttle Crab - Aquatic river crab with slick teal shell & stalks.
   */
  static createScuttleCrabMesh() {
    const group = new Group();
    const shellMat = new MeshStandardMaterial({ color: 0x0d9488, roughness: 0.25, metalness: 0.1 });
    const eyeMat = new MeshStandardMaterial({ color: 0x5eead4, emissive: 0x14b8a6, emissiveIntensity: 0.6 });

    // Main Shell
    const shell = new Mesh(new SphereGeometry(0.7, 14, 10), shellMat);
    shell.scale.set(1.4, 0.6, 1.1);
    shell.position.y = 0.5;
    group.add(shell);

    // Eye Stalks & Glowing Eyes
    const leftEye = new Mesh(new SphereGeometry(0.12, 8, 8), eyeMat);
    leftEye.position.set(-0.35, 0.9, 0.7);
    group.add(leftEye);

    const rightEye = new Mesh(new SphereGeometry(0.12, 8, 8), eyeMat);
    rightEye.position.set(0.35, 0.9, 0.7);
    group.add(rightEye);

    // Pincers
    const leftPincer = new Mesh(new BoxGeometry(0.3, 0.2, 0.4), shellMat);
    leftPincer.position.set(-0.85, 0.45, 0.6);
    group.add(leftPincer);

    const rightPincer = new Mesh(new BoxGeometry(0.3, 0.2, 0.4), shellMat);
    rightPincer.position.set(0.85, 0.45, 0.6);
    group.add(rightPincer);

    group.userData.update = (dt, elapsed = 0) => {
      shell.position.y = 0.5 + Math.sin(elapsed * 6) * 0.04;
      leftPincer.rotation.y = Math.sin(elapsed * 4) * 0.15;
      rightPincer.rotation.y = -Math.sin(elapsed * 4) * 0.15;
    };

    return group;
  }

  /**
   * Dragon - Elemental Dragon with winged geometry, horns, tail & glowing fiery mouth.
   */
  static createDragonMesh() {
    const group = new Group();
    const bodyMat = new MeshStandardMaterial({ color: 0xb91c1c, roughness: 0.5 });
    const wingMat = new MeshStandardMaterial({ color: 0x7f1d1d, side: DoubleSide });
    const fireMat = new MeshStandardMaterial({ color: 0xf97316, emissive: 0xd97706, emissiveIntensity: 0.9 });

    // Dragon Torso & Neck
    const body = new Mesh(new ConeGeometry(0.9, 2.8, 8), bodyMat);
    body.rotation.x = Math.PI / 3;
    body.position.set(0, 1.8, -0.2);
    group.add(body);

    // Head with Glowing Jaw
    const head = new Mesh(new ConeGeometry(0.45, 1.2, 6), bodyMat);
    head.rotation.x = Math.PI / 2;
    head.position.set(0, 2.9, 0.9);
    group.add(head);

    const mawGlow = new Mesh(new SphereGeometry(0.25, 8, 8), fireMat);
    mawGlow.position.set(0, 2.7, 1.1);
    group.add(mawGlow);

    // Broad Wings
    const wingsGroup = new Group();
    wingsGroup.position.set(0, 2.2, 0);

    const leftWing = new Mesh(new PlaneGeometry(2.4, 1.4), wingMat);
    leftWing.position.set(-1.4, 0, 0);
    leftWing.rotation.y = -Math.PI / 6;
    wingsGroup.add(leftWing);

    const rightWing = new Mesh(new PlaneGeometry(2.4, 1.4), wingMat);
    rightWing.position.set(1.4, 0, 0);
    rightWing.rotation.y = Math.PI / 6;
    wingsGroup.add(rightWing);

    group.add(wingsGroup);

    group.userData.update = (dt, elapsed = 0) => {
      wingsGroup.rotation.z = Math.sin(elapsed * 3) * 0.15;
      group.position.y = Math.sin(elapsed * 2) * 0.12;
    };

    return group;
  }

  /**
   * Baron Nashor - Epic Void Serpent monstrosity with multiple eyes & glowing mandibles.
   */
  static createBaronMesh() {
    const group = new Group();
    const bodyMat = new MeshStandardMaterial({ color: 0x4c1d95, roughness: 0.4 });
    const voidMat = new MeshStandardMaterial({ color: 0xc084fc, emissive: 0xa855f7, emissiveIntensity: 0.9 });
    const teethMat = new MeshStandardMaterial({ color: 0xf1f5f9, roughness: 0.2 });

    // Massive Void Serpent Body Column
    const body = new Mesh(new CylinderGeometry(1.2, 1.8, 4.5, 12), bodyMat);
    body.position.y = 2.25;
    group.add(body);

    // Multi-jawed Void Head
    const head = new Mesh(new DodecahedronGeometry(1.1, 1), bodyMat);
    head.position.y = 4.8;
    group.add(head);

    // Glowing Void Eye Cluster
    const eyeCluster = new Mesh(new SphereGeometry(0.45, 12, 12), voidMat);
    eyeCluster.position.set(0, 5.0, 0.7);
    group.add(eyeCluster);

    // Teeth Rings
    const teeth = new Mesh(new TorusGeometry(0.6, 0.1, 8, 16), teethMat);
    teeth.rotation.x = Math.PI / 2;
    teeth.position.set(0, 4.4, 0.6);
    group.add(teeth);

    group.userData.update = (dt, elapsed = 0) => {
      body.rotation.y = Math.sin(elapsed * 1.2) * 0.1;
      head.position.x = Math.sin(elapsed * 1.5) * 0.15;
      voidMat.emissiveIntensity = 0.7 + 0.3 * Math.sin(elapsed * 3);
    };

    return group;
  }

  /**
   * Gromp - Large toxic toad with glowing poison sacs.
   */
  static createGrompMesh() {
    const group = new Group();
    const skinMat = new MeshStandardMaterial({ color: 0x14532d, roughness: 0.7 });
    const poisonMat = new MeshStandardMaterial({ color: 0x4ade80, emissive: 0x22c55e, emissiveIntensity: 0.7 });

    const body = new Mesh(new SphereGeometry(1.1, 14, 12), skinMat);
    body.scale.set(1.2, 0.9, 1.1);
    body.position.y = 0.9;
    group.add(body);

    // Poison Pustules
    const pustule1 = new Mesh(new SphereGeometry(0.25, 8, 8), poisonMat);
    pustule1.position.set(-0.4, 1.5, 0.2);
    group.add(pustule1);

    const pustule2 = new Mesh(new SphereGeometry(0.2, 8, 8), poisonMat);
    pustule2.position.set(0.4, 1.4, -0.1);
    group.add(pustule2);

    return group;
  }

  /**
   * Wolf Camp - Alpha & Small Wolves with detailed snouts & glowing eyes.
   */
  static createWolfMesh(variant = 'alpha') {
    const group = new Group();
    const isAlpha = variant === 'alpha';
    const scale = isAlpha ? 1.2 : 0.8;

    const furMat = new MeshStandardMaterial({ color: isAlpha ? 0x334155 : 0x475569, roughness: 0.8 });
    const eyeMat = new MeshStandardMaterial({ color: 0xfde047, emissive: 0xeab308, emissiveIntensity: 0.8 });

    // Wolf Body
    const body = new Mesh(new CylinderGeometry(0.45 * scale, 0.35 * scale, 1.4 * scale, 8), furMat);
    body.rotation.x = Math.PI / 3;
    body.position.y = 0.7 * scale;
    group.add(body);

    // Head & Snout
    const head = new Mesh(new ConeGeometry(0.35 * scale, 0.8 * scale, 6), furMat);
    head.rotation.x = Math.PI / 2;
    head.position.set(0, 1.1 * scale, 0.6 * scale);
    group.add(head);

    const leftEye = new Mesh(new SphereGeometry(0.08 * scale, 6, 6), eyeMat);
    leftEye.position.set(-0.18 * scale, 1.2 * scale, 0.6 * scale);
    group.add(leftEye);

    const rightEye = new Mesh(new SphereGeometry(0.08 * scale, 6, 6), eyeMat);
    rightEye.position.set(0.18 * scale, 1.2 * scale, 0.6 * scale);
    group.add(rightEye);

    return group;
  }

  /**
   * Razorbeak - Feathery raptor.
   */
  static createRazorbeakMesh() {
    const group = new Group();
    const featherMat = new MeshStandardMaterial({ color: 0x9a3412, roughness: 0.6 });
    const crestMat = new MeshStandardMaterial({ color: 0xf97316, emissive: 0xeab308, emissiveIntensity: 0.5 });

    const body = new Mesh(new SphereGeometry(0.55, 10, 10), featherMat);
    body.position.y = 0.8;
    group.add(body);

    const crest = new Mesh(new ConeGeometry(0.2, 0.6, 6), crestMat);
    crest.position.set(0, 1.4, 0.1);
    crest.rotation.x = -0.3;
    group.add(crest);

    return group;
  }
}
